import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import ThemeToggle from '../components/ThemeToggle';
import { 
    Search, 
    FileText, 
    CheckCircle2, 
    MapPin, 
    Users,
    ArrowRight,
    HardHat,
    Shield
} from 'lucide-react';

const Landing = () => {
    const features = [
        {
            icon: Search,
            title: 'Search Addresses',
            description: 'Look up any job address to see contractor-reported job experiences before accepting work.'
        },
        {
            icon: FileText,
            title: 'Structured Reviews',
            description: 'Submit detailed, professional reviews based on real job experiences - not emotional rants.'
        },
        {
            icon: Shield,
            title: 'Experience Summaries',
            description: 'See aggregated contractor feedback based on payment history, communication, and professionalism.'
        },
        {
            icon: MapPin,
            title: 'Address-Based',
            description: 'Reviews tied to properties, not people. Track experiences at specific job sites.'
        }
    ];

    const stats = [
        { value: '100%', label: 'Address Focused' },
        { value: 'Real', label: 'Contractor Data' },
        { value: 'Smart', label: 'Business Decisions' }
    ];

    return (
        <div className="min-h-screen bg-background theme-transition">
            {/* Navigation */}
            <nav className="absolute top-0 left-0 right-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <Logo variant="full" size="default" />
                        <div className="flex items-center gap-3">
                            <ThemeToggle />
                            <Link to="/login">
                                <Button variant="ghost" className="text-foreground hover:bg-accent">
                                    Log In
                                </Button>
                            </Link>
                            <Link to="/signup">
                                <Button className="bg-primary hover:bg-primary/90">
                                    Get Started
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative overflow-hidden pt-20">
                {/* Background */}
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-15"
                    style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1540103711724-ebf833bde8d1?crop=entropy&cs=srgb&fm=jpg&q=85)'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
                
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
                    <div className="text-center">
                        <div className="flex justify-center mb-8">
                            <Logo variant="icon" size="hero" />
                        </div>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight">
                            Know Your Jobs
                            <span className="block text-primary mt-2">Before You Accept</span>
                        </h1>
                        <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            ClientCheck helps contractors make smarter business decisions by sharing 
                            real experiences at job addresses. Avoid problem jobs. Protect your business.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/signup">
                                <Button 
                                    size="lg" 
                                    className="w-full sm:w-auto text-lg px-8 bg-primary hover:bg-primary/90 active:scale-95 transition-transform"
                                    data-testid="hero-signup-btn"
                                >
                                    <HardHat className="mr-2 h-5 w-5" />
                                    Join Free
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button 
                                    size="lg" 
                                    variant="outline"
                                    className="w-full sm:w-auto text-lg px-8"
                                    data-testid="hero-login-btn"
                                >
                                    Sign In
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="border-y border-border bg-card/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-3 gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="font-heading font-extrabold text-3xl sm:text-4xl text-primary">
                                    {stat.value}
                                </div>
                                <div className="mt-1 text-sm sm:text-base text-muted-foreground">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-foreground">
                            Built for Contractors
                        </h2>
                        <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                            Professional tools to help you evaluate jobs and share experiences with other contractors.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {features.map((feature, index) => (
                            <Card 
                                key={index} 
                                className="bg-card border-border hover:border-muted-foreground transition-colors"
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 rounded-lg bg-primary/10">
                                            <feature.icon className="h-6 w-6 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="font-heading font-bold text-lg text-foreground">
                                                {feature.title}
                                            </h3>
                                            <p className="mt-2 text-muted-foreground">
                                                {feature.description}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-20 bg-card/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-foreground">
                            How It Works
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                step: '01',
                                title: 'Search Address',
                                description: 'Enter any job address to check for existing contractor-reported job experiences.'
                            },
                            {
                                step: '02',
                                title: 'Review Experience Summary',
                                description: 'See aggregated contractor feedback based on payment history, communication, and past experiences.'
                            },
                            {
                                step: '03',
                                title: 'Make Smart Decisions',
                                description: 'Use real contractor data to decide whether to accept or decline job opportunities.'
                            }
                        ].map((item, index) => (
                            <div key={index} className="text-center">
                                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/20 mb-4">
                                    <span className="font-heading font-extrabold text-2xl text-primary">
                                        {item.step}
                                    </span>
                                </div>
                                <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-muted-foreground">
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Card className="bg-primary/10 border-primary/30">
                        <CardContent className="p-8 sm:p-12 text-center">
                            <Logo variant="icon" size="large" className="mx-auto mb-4" />
                            <h2 className="font-heading font-black text-2xl sm:text-3xl text-foreground mb-4">
                                Join the Contractor Network
                            </h2>
                            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                                Help fellow contractors make better decisions. Share your job experiences 
                                and access valuable insights from the community.
                            </p>
                            <Link to="/signup">
                                <Button 
                                    size="lg" 
                                    className="text-lg px-8 bg-primary hover:bg-primary/90 active:scale-95 transition-transform"
                                    data-testid="cta-signup-btn"
                                >
                                    <CheckCircle2 className="mr-2 h-5 w-5" />
                                    Create Free Account
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Landing;
